/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { TDictionary } from '../../../util/types';

export type T_Dictionary = TDictionary<unknown>;

export function sortMonths(months: string[]) {
  const currentMonthIndex = new Date().getMonth();
  const sortedMonths = months
    .slice(currentMonthIndex + 1)
    .concat(months.slice(0, currentMonthIndex + 1));
  return sortedMonths;
}

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function convertMonthToString(spreadMonth: Date) {
  // Get the month index and year from the spreadMonth Date object
  const monthIndex = spreadMonth.getMonth();
  const year = spreadMonth.getFullYear();

  // Create a Date object using the month index and year
  const date = new Date(year, monthIndex, 1);

  // Get the month name from the Date object
  const monthName = date.toLocaleDateString('en-US', { month: 'long' });

  return monthName;
}

export function spreadEvenly(
  totalAmount: number,
  maxSpread: number,
  startMonth: Date,
): { month: Date; quantity: number }[] {
  const currentMonth = startMonth.getMonth();
  const currentYear = startMonth.getFullYear();
  const spread = Array.from({ length: maxSpread }, (_, index) => {
    const monthIndex = (currentMonth + index) % 12; // Get the next month index, wrapping around to 0 if necessary
    const yearOffset = Math.floor((currentMonth + index + 1) / 12); // Adjust year if the next month goes into next year
    const year = currentYear + yearOffset;
    const month = new Date(startMonth);
    month.setMonth(monthIndex);
    month.setFullYear(year);
    month.setDate(1);
    return { month: month, quantity: 0 };
  });

  let currentAmount = totalAmount;
  let currentSpreadIndex = 0;
  while (currentAmount > 0) {
    spread[currentSpreadIndex].quantity++;
    currentSpreadIndex++;
    if (currentSpreadIndex >= maxSpread) {
      currentSpreadIndex = 0;
    }
    currentAmount--;
  }

  return spread;
}

export function calculateEndDate(startMonth: Date, maxSpread: number): string {
  const currentMonth = startMonth.getMonth();
  const currentYear = startMonth.getFullYear();

  // Calculate the end month index by adding maxSpread - 1 to the start month index
  const endMonthIndex = (currentMonth + maxSpread - 1) % 12;

  // Calculate the end year by adding the quotient of (currentMonth + maxSpread - 1) divided by 12
  const yearOffset = Math.floor((currentMonth + maxSpread - 1) / 12);
  const endYear = currentYear + yearOffset;

  // Construct and return the string representing the start and end months and years
  const startDateString = startMonth.toLocaleDateString('en-NZ', {
    month: 'long',
    year: 'numeric',
  });
  const endDateString = new Date(endYear, endMonthIndex, 1).toLocaleDateString('en-NZ', {
    month: 'long',
    year: 'numeric',
  });

  return `${startDateString} - ${endDateString}`;
}
